import React, { useRef } from 'react';
import { FaPenFancy, FaMagic, FaRocket } from 'react-icons/fa';
import RoughBorder from '../../kits/LayoutKit/RoughBorder';

/**
 * Wiederverwendbare ZenPost Workflow-Section Komponente
 * Zeigt den 3-Schritte Workflow: Schreiben → Transformieren → Publizieren
 */
const ZenPostWorkflowSection = ({
  title = "Dein neuer Workflow",
  subtitle = "Von der Idee zur globalen Publikation in Minuten, nicht Stunden.",
  steps = null,
  variant = "cards"
}) => {
  const workflowCard1Ref = useRef();
  const workflowCard2Ref = useRef();
  const workflowCard3Ref = useRef();

  // Default Workflow Steps
  const defaultSteps = [
    {
      icon: <FaPenFancy className="text-brand" />,
      
      title: "1. Schreiben",
      description: "Schreibe deinen Content einmal in Markdown oder EditorJS. Mit KI-Unterstützung.",
      features: [
        "KI-Textverbesserung",
        "Grammatik & Stil",
        "Content-Ideen"
      ],
      bgColor: "bg-brand/5",
      borderColor: "border-brand/20",
      hoverBorderColor: "hover:border-brand",
      ref: workflowCard1Ref
    },
    {
      icon: <FaMagic className="text-purple-600" />,
     
      title: "2. Transformieren",
      description: "Passe Tonalität, Länge und Zielgruppe mit lokaler KI an.",
      features: [
        "Professional → Casual",
        "Lang → Twitter-Thread",
        "Tech → Business"
      ],
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      hoverBorderColor: "hover:border-purple-400",
      ref: workflowCard2Ref
    },
    {
      icon: <FaRocket className="text-green-600" />,
      
      title: "3. Publizieren",
      description: "Ein Klick. 9+ Plattformen. Automatisch formatiert.",
      features: [
        "LinkedIn Post",
        "dev.to Artikel",
        "Twitter Thread"
      ],
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      hoverBorderColor: "hover:border-green-400",
      ref: workflowCard3Ref
    }
  ];

  const workflowSteps = steps || defaultSteps;

  if (variant === "timeline") {
    return (
      <section className="py-16 sm:py-20 px-4 text-brand">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-14 px-4">
            <div className="inline-flex items-center rounded-xl border border-brand  px-3 py-1 text-xs font-semibold text-white">
              How it works
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold">
              {title}
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-300 max-w-3xl mx-auto">
              {subtitle}
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-0 right-0 top-8 h-px bg-gradient-to-r from-brand via-violet-500/30 to-transparent" />
            <div className="grid gap-8 md:grid-cols-4">
              {workflowSteps.map((step, index) => (
                <div key={index} className="relative text-center md:text-left">
                  <div className="mx-auto md:mx-0 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-lg font-bold text-white shadow-lg shadow-fuchsia-500/30">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <h3 className="mt-5 text-lg sm:text-xl font-semibold text-white">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm sm:text-base text-slate-300">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 sm:py-16 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 px-4">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 mb-3 sm:mb-4">
            {title}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
          {workflowSteps.map((step, index) => (
            <div key={index} ref={step.ref} className="relative h-full">
              <div className={`${step.bgColor} p-4 sm:p-6 md:p-8 rounded-lg border-2 ${step.borderColor} ${step.hoverBorderColor} transition-colors h-full flex flex-col`}>
                <div className="flex items-center gap-3 mb-3 sm:mb-4">
                  <div className="text-3xl sm:text-4xl">{step.emoji}</div>
                  <div className="text-2xl sm:text-3xl">{step.icon}</div>
                </div>

                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                  {step.title}
                </h3>

                <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                  {step.description}
                </p>

                <ul className="text-xs sm:text-sm text-gray-600 space-y-1 sm:space-y-2 mt-auto">
                  {step.features.map((feature, i) => (
                    <li key={i}>• {feature}</li>
                  ))}
                </ul>
              </div>

              <RoughBorder
                parentRef={step.ref}
                radius={10}
                padding={2}
                color="#AC8E66"
                strokeWidth={0.3}
                roughness={1}
                bowing={2}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ZenPostWorkflowSection;
